// Lead + newsletter capture via Netlify Forms.
//
// No backend, no database. Submissions land in the Netlify dashboard under
// Forms, and Netlify can email or webhook them onward. Field names here must
// match the hidden form stubs in public/index.html.
//
// To move to a CRM or webhook later, reimplement postToNetlify() only —
// the exported functions below are the stable interface the UI calls.

const encode = (data) =>
  Object.keys(data)
    .map(
      (k) =>
        `${encodeURIComponent(k)}=${encodeURIComponent(
          data[k] === null || data[k] === undefined ? "" : String(data[k])
        )}`
    )
    .join("&");

async function postToNetlify(formName, fields) {
  const res = await fetch("/", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: encode({ "form-name": formName, ...fields }),
  });
  if (!res.ok) {
    throw new Error(`Submission failed (${res.status})`);
  }
  return { status: "ok" };
}

// --- Attribution -----------------------------------------------------------
// Ads send traffic to a URL with UTM parameters, but a visitor usually browses
// before converting, and those parameters only exist on the first URL. Capture
// them on arrival and keep them for the session, so a lead that started as a
// paid click is still recorded as one three pages later.

const ATTR_KEY = "dtb_attribution";

function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    };
    const hasUtm = Object.values(fromUrl).some(Boolean);
    // First touch wins: a later internal navigation must not overwrite the
    // campaign that actually brought them here.
    if (sessionStorage.getItem(ATTR_KEY)) return;
    if (!hasUtm && !document.referrer) return;

    sessionStorage.setItem(
      ATTR_KEY,
      JSON.stringify({
        ...fromUrl,
        referrer: document.referrer || null,
        landing_page: window.location.pathname,
      })
    );
  } catch {
    // Private browsing can block sessionStorage — attribution degrades to
    // whatever is on the URL at submit time. Never break the form over it.
  }
}

if (typeof window !== "undefined") captureAttribution();

export function getAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    let stored = {};
    try {
      stored = JSON.parse(sessionStorage.getItem(ATTR_KEY) || "{}");
    } catch {
      stored = {};
    }
    return {
      // Current URL wins if present, else the session's first touch.
      utm_source: params.get("utm_source") || stored.utm_source || null,
      utm_medium: params.get("utm_medium") || stored.utm_medium || null,
      utm_campaign: params.get("utm_campaign") || stored.utm_campaign || null,
      referrer: stored.referrer || document.referrer || null,
      page: window.location.pathname,
    };
  } catch {
    return {};
  }
}

// Drops null/undefined/"" so a defaulted value never overwrites a real one.
function defined(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );
}

export async function submitLead(payload) {
  // Attribution first, caller's explicit values second: a page that knows it is
  // a paid funnel must be able to say so without an empty URL param erasing it.
  const fields = { ...defined(getAttribution()), ...defined(payload) };
  fields.consent = payload.consent ? "yes" : "no";
  return postToNetlify("lead", fields);
}

// Pest-control companies applying to buy leads. A separate Netlify form, so
// submission-created ignores it and it is never routed as a homeowner lead.
export async function submitProApplication(payload) {
  const fields = { ...defined(getAttribution()), ...defined(payload) };
  return postToNetlify("pro", fields);
}

export async function subscribeNewsletter(email, source) {
  await postToNetlify("newsletter", {
    email,
    source,
    page: typeof window !== "undefined" ? window.location.pathname : "",
  });
  // Netlify does not de-duplicate, so there is no "already subscribed" state.
  return { status: "subscribed" };
}

// --- Admin ---
// Reads the durable lead record written by the submission-created function.
// Auth is a bearer token (ADMIN_TOKEN env var on Netlify).

const ADMIN_ENDPOINT = "/api/leads";

async function adminRequest(token, options = {}) {
  const res = await fetch((options.endpoint || ADMIN_ENDPOINT) + (options.query || ""), {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

export async function verifyAdmin(token) {
  await adminRequest(token, { query: "?limit=1" });
  return { ok: true };
}

export async function fetchLeads(token) {
  return adminRequest(token);
}

export async function updateLead(token, leadKey, changes) {
  return adminRequest(token, { method: "PATCH", body: { key: leadKey, ...changes } });
}

export async function deleteLead(token, leadKey) {
  return adminRequest(token, { method: "DELETE", body: { key: leadKey } });
}

// Pro applications from the /pros page, written by submission-created.
const PROS_ENDPOINT = "/api/pros";

export async function fetchPros(token) {
  return adminRequest(token, { endpoint: PROS_ENDPOINT });
}

export async function updatePro(token, key, changes) {
  return adminRequest(token, { endpoint: PROS_ENDPOINT, method: "PATCH", body: { key, ...changes } });
}

export async function deletePro(token, key) {
  return adminRequest(token, { endpoint: PROS_ENDPOINT, method: "DELETE", body: { key } });
}
